/**
 * WebRTC Peer Connection Manager for Canasta multiplayer video chat
 *
 * Handles peer-to-peer connections between players for video/audio streaming.
 * Ported from vanilla JS to TypeScript for better type safety and maintainability.
 *
 * Features:
 * - RTCPeerConnection management for each player
 * - WebRTC signaling via Socket.IO (offers, answers, ICE candidates)
 * - Glare handling (simultaneous connection attempts)
 * - Track management and renegotiation
 * - Connection state monitoring
 * - Bot/spectator filtering
 */

import { Logger, LogCategory } from '@/utils/Logger.class'

// Lazy-initialize logger to avoid "Cannot access before initialization" errors
let rtcLog: ReturnType<typeof Logger.scope> | null = null
function getLogger() {
  if (!rtcLog) {
    rtcLog = Logger.scope(LogCategory.WEBRTC)
  }
  return rtcLog
}

/**
 * Video state for a player
 */
export interface VideoState {
  video_enabled: boolean
  audio_enabled: boolean
}

/**
 * Socket interface for WebRTC signaling
 */
interface WebRTCSocket {
  id?: string
  connected?: boolean
  emit: (event: string, data?: any) => void
  on: (event: string, handler: (...args: any[]) => void) => void
  off: (event: string, handler?: (...args: any[]) => void) => void
}

/**
 * ICE server configuration for NAT traversal
 *
 * SECURITY: TURN servers protect player IP addresses by relaying traffic.
 * Configure via environment variables for production use.
 */
function getIceServers(): RTCConfiguration {
  const iceServers: RTCIceServer[] = [
    // Public STUN servers (for NAT traversal discovery)
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
  ]

  // SECURITY: Add TURN server if configured (protects player IPs)
  const turnUrl = import.meta.env.VITE_TURN_URL
  const turnUsername = import.meta.env.VITE_TURN_USERNAME
  const turnCredential = import.meta.env.VITE_TURN_CREDENTIAL

  if (turnUrl && turnUsername && turnCredential) {
    iceServers.push({
      urls: turnUrl,
      username: turnUsername,
      credential: turnCredential,
    })
    getLogger().info('TURN server configured for IP privacy protection')
  } else {
    getLogger().warn(
      'TURN server not configured - player IP addresses may be exposed to peers. ' +
      'Set VITE_TURN_URL, VITE_TURN_USERNAME, and VITE_TURN_CREDENTIAL for production.'
    )
  }

  return { iceServers }
}

// Cached ICE configuration
let ICE_SERVERS: RTCConfiguration | null = null
function getIceConfiguration(): RTCConfiguration {
  if (!ICE_SERVERS) {
    ICE_SERVERS = getIceServers()
  }
  return ICE_SERVERS
}

/**
 * WebRTC Manager Class
 *
 * Manages peer-to-peer connections for video/audio streaming between players.
 */
export class WebRTCManager {
  // Connection maps
  private peerConnections: Map<string, RTCPeerConnection> = new Map()
  private remoteStreams: Map<string, MediaStream> = new Map()
  private playerVideoStates: Map<string, VideoState> = new Map()
  private connectionStates: Map<string, RTCPeerConnectionState> = new Map()

  // Local state
  private localStream: MediaStream | null = null
  private myPlayerId: string | null = null
  private otherPlayerIds: string[] = []
  private socket: WebRTCSocket | null = null

  // Handler tracking for cleanup
  private handlersSetup = false
  private webrtcOfferHandler: ((data: any) => void) | null = null
  private webrtcAnswerHandler: ((data: any) => void) | null = null
  private webrtcIceCandidateHandler: ((data: any) => void) | null = null
  private playerVideoStateHandler: ((data: any) => void) | null = null
  private playerVideoStatesHandler: ((data: any) => void) | null = null
  private playerJoinedHandler: ((data: any) => void) | null = null
  private playerReconnectedHandler: ((data: any) => void) | null = null

  // Reconnection tracking
  private pendingReconnections: Set<string> = new Set()

  // Connection attempt tracking - prevents duplicate/overlapping attempts
  private pendingConnectionAttempts: Set<string> = new Set()
  private lastConnectionAttempt: Map<string, number> = new Map()
  private static CONNECTION_COOLDOWN_MS = 3000 // Minimum 3s between connection attempts to same player

  // SECURITY: Known game participants (verified by server)
  // Only accept WebRTC signals from these player IDs
  private validParticipants: Set<string> = new Set()

  // ICE candidate queue (buffer candidates until remote description is set)
  private pendingCandidates: Map<string, RTCIceCandidateInit[]> = new Map()

  // Stream debounce timeouts
  private streamTimeouts: Map<string, ReturnType<typeof setTimeout>> = new Map()

  // Disconnect recovery timeouts (allow time to recover from 'disconnected' state)
  private disconnectTimeouts: Map<string, ReturnType<typeof setTimeout>> = new Map()

  // Callbacks
  onRemoteStream: ((playerId: string, stream: MediaStream) => void) | null = null
  onRemoteStreamEnded: ((playerId: string) => void) | null = null
  onConnectionStateChange: ((playerId: string, state: RTCPeerConnectionState) => void) | null = null

  /**
   * SECURITY: Set the list of valid game participants
   * Only WebRTC signals from these player IDs will be accepted.
   * Call this when game state is received from the server.
   */
  setValidParticipants(playerIds: string[]): void {
    this.validParticipants.clear()
    for (const id of playerIds) {
      if (id && !id.startsWith('bot_')) {
        this.validParticipants.add(id)
      }
    }
    getLogger().info('Valid WebRTC participants set:', Array.from(this.validParticipants))
  }

  /**
   * SECURITY: Add a participant to the valid list
   * Called when a new player joins the game.
   */
  addValidParticipant(playerId: string): void {
    if (playerId && !playerId.startsWith('bot_')) {
      this.validParticipants.add(playerId)
      getLogger().info('Added valid WebRTC participant:', playerId)
    }
  }

  /**
   * SECURITY: Check if a player ID is a valid participant
   * Prevents accepting WebRTC signals from unknown/malicious peers.
   */
  private isValidPeer(playerId: string): boolean {
    // Always reject if playerId is missing or is a bot
    if (!playerId || playerId.startsWith('bot_')) {
      return false
    }
    // If we haven't set any participants yet, allow (for backwards compat during init)
    if (this.validParticipants.size === 0) {
      getLogger().warn('No valid participants set - allowing peer (init phase):', playerId)
      return true
    }
    // Check if player is in valid participants list
    const isValid = this.validParticipants.has(playerId)
    if (!isValid) {
      getLogger().warn('SECURITY: Rejected WebRTC signal from unknown peer:', playerId)
    }
    return isValid
  }

  onVideoStateChange: ((playerId: string, state: VideoState) => void) | null = null
  onConnectionFailed: ((playerId: string) => void) | null = null

  constructor() {
    getLogger().debug('WebRTCManager constructed')
  }

  /**
   * Initialize with socket connection and player info
   */
  init(socket: WebRTCSocket, myPlayerId: string): void {
    getLogger().info('WebRTCManager.init called', { playerId: myPlayerId, socketId: socket?.id })

    // Don't initialize WebRTC for spectators
    if (typeof window !== 'undefined' && (window as any).isSpectator) {
      getLogger().info('Skipping WebRTC init for spectator')
      return
    }

    const socketChanged = this.socket !== socket
    this.socket = socket
    this.myPlayerId = myPlayerId

    // Setup handlers if socket changed or not yet setup
    if (socketChanged || !this.handlersSetup) {
      this.setupSignalingHandlers()
    }

    getLogger().info('WebRTCManager initialized', { playerId: myPlayerId, socketConnected: socket?.connected })
  }

  /**
   * Set up socket event handlers for WebRTC signaling
   */
  private setupSignalingHandlers(): void {
    if (!this.socket) {
      getLogger().error('Socket not available for WebRTC signaling')
      return
    }

    // Remove existing handlers to avoid duplicates
    this.removeSignalingHandlers()

    // Handle incoming WebRTC offer
    this.webrtcOfferHandler = async (data: any) => {
      // SECURITY: Validate peer before processing offer
      if (!this.isValidPeer(data.from_player_id)) {
        getLogger().warn('SECURITY: Ignoring WebRTC offer from invalid peer:', data.from_player_id)
        return
      }
      getLogger().info('RECEIVED WebRTC OFFER from:', data.from_player_id)
      await this.handleOffer(data.from_player_id, data.offer)
    }
    this.socket.on('webrtc_offer', this.webrtcOfferHandler)

    // Handle incoming WebRTC answer
    this.webrtcAnswerHandler = async (data: any) => {
      // SECURITY: Validate peer before processing answer
      if (!this.isValidPeer(data.from_player_id)) {
        getLogger().warn('SECURITY: Ignoring WebRTC answer from invalid peer:', data.from_player_id)
        return
      }
      getLogger().info('Received WebRTC answer from:', data.from_player_id)
      await this.handleAnswer(data.from_player_id, data.answer)
    }
    this.socket.on('webrtc_answer', this.webrtcAnswerHandler)

    // Handle incoming ICE candidate
    this.webrtcIceCandidateHandler = async (data: any) => {
      // SECURITY: Validate peer before processing ICE candidate
      if (!this.isValidPeer(data.from_player_id)) {
        getLogger().warn('SECURITY: Ignoring ICE candidate from invalid peer:', data.from_player_id)
        return
      }
      getLogger().debug('Received ICE candidate from:', data.from_player_id)
      await this.handleIceCandidate(data.from_player_id, data.candidate)
    }
    this.socket.on('webrtc_ice_candidate', this.webrtcIceCandidateHandler)

    // Handle player video state changes (single player update)
    this.playerVideoStateHandler = (data: any) => {
      getLogger().info('Player video state changed:', data)
      this.handlePlayerVideoState(data)
    }
    this.socket.on('player_video_state', this.playerVideoStateHandler)

    // Handle bulk video states (on join/reconnect)
    this.playerVideoStatesHandler = (data: any) => {
      getLogger().info('Received bulk video states:', data)
      if (data.states) {
        for (const state of data.states) {
          this.handlePlayerVideoState(state)
        }
      }
    }
    this.socket.on('player_video_states', this.playerVideoStatesHandler)

    // Handle new player joining
    this.playerJoinedHandler = async (data: any) => {
      const { player_id } = data
      if (!player_id || player_id === this.myPlayerId) return
      if (player_id.startsWith('bot_')) return

      getLogger().info('New player joined:', player_id)

      if (!this.otherPlayerIds.includes(player_id)) {
        this.otherPlayerIds.push(player_id)
      }

      // If we have video enabled, initiate connection to new player
      if (this.localStream && !this.peerConnections.has(player_id)) {
        getLogger().info('We have video, initiating connection to new player:', player_id)
        await this.initiateConnection(player_id)
      }
    }
    this.socket.on('player_joined', this.playerJoinedHandler)

    // Handle player reconnecting
    this.playerReconnectedHandler = async (data: any) => {
      const { player_id } = data
      if (!player_id || player_id === this.myPlayerId) return
      if (player_id.startsWith('bot_')) return

      getLogger().info('Player reconnected:', player_id)

      if (!this.otherPlayerIds.includes(player_id)) {
        this.otherPlayerIds.push(player_id)
      }

      // Close any existing stale connection
      if (this.peerConnections.has(player_id)) {
        const oldPc = this.peerConnections.get(player_id)!
        oldPc.close()
        this.peerConnections.delete(player_id)
        this.remoteStreams.delete(player_id)

        if (this.onRemoteStreamEnded) {
          this.onRemoteStreamEnded(player_id)
        }

        getLogger().info('Closed old peer connection for reconnected player', player_id)
      }

      // Wait for player's video state update before reconnecting
      this.pendingReconnections.add(player_id)

      // Timeout after 5 seconds
      setTimeout(() => {
        if (this.pendingReconnections.has(player_id)) {
          getLogger().warn('Timeout waiting for video state from reconnected player', player_id)
          this.pendingReconnections.delete(player_id)
        }
      }, 5000)
    }
    this.socket.on('player_reconnected', this.playerReconnectedHandler)

    this.handlersSetup = true
    getLogger().info('WebRTC signaling handlers set up', { socketId: this.socket.id })
  }

  /**
   * Remove signaling handlers
   */
  private removeSignalingHandlers(): void {
    if (!this.socket) return

    if (this.webrtcOfferHandler) this.socket.off('webrtc_offer', this.webrtcOfferHandler)
    if (this.webrtcAnswerHandler) this.socket.off('webrtc_answer', this.webrtcAnswerHandler)
    if (this.webrtcIceCandidateHandler) this.socket.off('webrtc_ice_candidate', this.webrtcIceCandidateHandler)
    if (this.playerVideoStateHandler) this.socket.off('player_video_state', this.playerVideoStateHandler)
    if (this.playerVideoStatesHandler) this.socket.off('player_video_states', this.playerVideoStatesHandler)
    if (this.playerJoinedHandler) this.socket.off('player_joined', this.playerJoinedHandler)
    if (this.playerReconnectedHandler) this.socket.off('player_reconnected', this.playerReconnectedHandler)
  }

  /**
   * Handle a player's video state change
   */
  private handlePlayerVideoState(data: any): void {
    const { player_id, video_enabled, audio_enabled } = data

    // VIDEO-9A: Raw data received
    console.log('[VIDEO-9A] handlePlayerVideoState called', {
      player_id,
      video_enabled,
      audio_enabled,
      myPlayerId: this.myPlayerId,
      isOwnState: player_id === this.myPlayerId
    })

    // Don't process our own state
    if (player_id === this.myPlayerId) {
      console.log('[VIDEO-9A] Skipping own state')
      return
    }

    getLogger().info('Player video state changed', {
      playerId: player_id,
      videoEnabled: video_enabled,
      audioEnabled: audio_enabled,
      isPending: this.pendingReconnections.has(player_id)
    })

    // Track the player's video state
    const state: VideoState = { video_enabled, audio_enabled }
    this.playerVideoStates.set(player_id, state)

    // Notify callback
    if (this.onVideoStateChange) {
      this.onVideoStateChange(player_id, state)
    }

    // Add to player list if not already there
    if (!this.otherPlayerIds.includes(player_id)) {
      this.otherPlayerIds.push(player_id)
      getLogger().debug('Added player to list:', player_id, 'Total:', this.otherPlayerIds.length)
    }

    // Clear pending reconnection flag
    if (this.pendingReconnections.has(player_id)) {
      this.pendingReconnections.delete(player_id)
      getLogger().info('Reconnected player video state received, will initiate connection', player_id)
    }

    // If they have video OR audio enabled and we don't have a connection
    if ((video_enabled || audio_enabled) && !this.peerConnections.has(player_id)) {
      // VIDEO-9B: Remote player has video/audio, checking if we should connect
      console.log('[VIDEO-9B] Remote player has video/audio, checking connection', {
        player_id,
        hasLocalStream: !!this.localStream,
        localStreamTracks: this.localStream?.getTracks().map(t => ({ kind: t.kind, enabled: t.enabled })),
        existingConnections: Array.from(this.peerConnections.keys())
      })

      getLogger().info('Player has video enabled, checking if we should connect:', player_id)

      if (this.localStream) {
        // VIDEO-9C: We have video too, initiating connection
        console.log('[VIDEO-9C] We have video too, initiating connection to', player_id)
        getLogger().info('We also have video, initiating connection to:', player_id)
        this.initiateConnection(player_id)
      } else {
        // VIDEO-9D: We don't have video, waiting for theirs
        console.log('[VIDEO-9D] We do NOT have video - will receive from', player_id, 'when they initiate')
        getLogger().debug('Ready to receive video from:', player_id)
      }
    } else {
      // VIDEO-9E: Not initiating connection
      console.log('[VIDEO-9E] Not initiating connection', {
        player_id,
        video_enabled,
        alreadyHasConnection: this.peerConnections.has(player_id)
      })
    }

    // If they disabled both video AND audio, clear their remote stream but keep connection
    if (!video_enabled && !audio_enabled && this.remoteStreams.has(player_id)) {
      getLogger().info('Player disabled video and audio, clearing their stream:', player_id)
      const stream = this.remoteStreams.get(player_id)
      if (stream) {
        stream.getTracks().forEach(track => track.stop())
      }
      this.remoteStreams.delete(player_id)

      if (this.onRemoteStreamEnded) {
        this.onRemoteStreamEnded(player_id)
      }
    }
  }

  /**
   * Set the local media stream to share
   */
  setLocalStream(stream: MediaStream | null): void {
    this.localStream = stream
    getLogger().debug('Local stream set:', stream?.getTracks().map(t => t.kind))

    // Add tracks to existing peer connections and renegotiate
    this.peerConnections.forEach(async (pc, playerId) => {
      const existingSenders = pc.getSenders()
      const hadVideoSender = existingSenders.some(s => s.track?.kind === 'video')
      const hadAudioSender = existingSenders.some(s => s.track?.kind === 'audio')
      const hasNewVideoTrack = stream?.getVideoTracks().some(t => t.enabled) ?? false
      const hasNewAudioTrack = stream?.getAudioTracks().some(t => t.enabled) ?? false

      console.log('[AUDIO-TX-1] setLocalStream track analysis', {
        playerId,
        hadVideoSender,
        hadAudioSender,
        hasNewVideoTrack,
        hasNewAudioTrack,
        streamTracks: stream?.getTracks().map(t => ({ kind: t.kind, enabled: t.enabled }))
      })

      this.addTracksToConnection(pc)

      // Renegotiate if we're adding NEW tracks that weren't there before
      // This fixes the bug where audio added after video never triggered renegotiation
      const addingNewVideo = !hadVideoSender && hasNewVideoTrack
      const addingNewAudio = !hadAudioSender && hasNewAudioTrack

      if (addingNewVideo || addingNewAudio) {
        console.log('[AUDIO-TX-3] Forcing renegotiation for new tracks', {
          playerId,
          addingNewVideo,
          addingNewAudio
        })
        getLogger().info('Renegotiating connection with:', playerId, { addingNewVideo, addingNewAudio })
        await this.renegotiateConnection(playerId, pc)
      }
    })
  }

  /**
   * Renegotiate an existing connection
   */
  private async renegotiateConnection(playerId: string, pc: RTCPeerConnection): Promise<void> {
    try {
      if (pc.signalingState !== 'stable') {
        getLogger().debug('Skipping renegotiation - connection not stable:', pc.signalingState)
        return
      }

      // Add local tracks before creating offer
      if (this.localStream) {
        this.addTracksToConnection(pc)
      }

      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)

      getLogger().info('Sending renegotiation offer to:', playerId)
      this.socket?.emit('webrtc_offer', {
        target_player_id: playerId,
        offer: pc.localDescription,
      })
    } catch (error) {
      getLogger().error('Error renegotiating connection:', error)
    }
  }

  /**
   * Update list of other players
   */
  updatePlayers(playerIds: string[]): void {
    this.otherPlayerIds = playerIds.filter(id =>
      id !== this.myPlayerId && !id.startsWith('bot_')
    )
    getLogger().info('Updated other players:', this.otherPlayerIds)

    this.requestVideoStates()
  }

  /**
   * Request current video states from server
   */
  requestVideoStates(): void {
    getLogger().debug('Requesting current video states from server')
    this.socket?.emit('request_video_states')
  }

  /**
   * Notify other players about our video/audio state change
   */
  notifyVideoStateChange(videoEnabled: boolean, audioEnabled: boolean): void {
    getLogger().info('Notifying video state change', { videoEnabled, audioEnabled })

    this.socket?.emit('video_state_changed', {
      video_enabled: videoEnabled,
      audio_enabled: audioEnabled,
    })

    // If we just enabled video OR audio, initiate connections to all other players
    if ((videoEnabled || audioEnabled) && this.localStream) {
      this.initiateConnections()
    }
  }

  /**
   * Initiate connections to all other players
   */
  initiateConnections(): void {
    getLogger().info('Initiating connections to', this.otherPlayerIds.length, 'players')

    for (const playerId of this.otherPlayerIds) {
      if (!this.peerConnections.has(playerId)) {
        this.initiateConnection(playerId)
      }
    }
  }

  /**
   * Initiate a peer connection to another player
   */
  private async initiateConnection(playerId: string): Promise<void> {
    // VIDEO-11: Entering initiateConnection
    console.log('[VIDEO-11] initiateConnection called', {
      targetPlayerId: playerId,
      myPlayerId: this.myPlayerId,
      hasExistingConnection: this.peerConnections.has(playerId),
      hasPendingAttempt: this.pendingConnectionAttempts.has(playerId),
      hasLocalStream: !!this.localStream,
      localStreamTracks: this.localStream?.getTracks().length
    })

    // Guard 1: Connection already exists
    if (this.peerConnections.has(playerId)) {
      console.log('[VIDEO-11] SKIP - Connection already exists to:', playerId)
      getLogger().debug('Connection already exists to:', playerId)
      return
    }

    // Guard 2: Connection attempt already in progress
    if (this.pendingConnectionAttempts.has(playerId)) {
      console.log('[VIDEO-11] SKIP - Connection attempt already in progress for:', playerId)
      getLogger().debug('Connection attempt already in progress for:', playerId)
      return
    }

    // Guard 3: Cooldown check - prevent rapid reconnection attempts
    const lastAttempt = this.lastConnectionAttempt.get(playerId) || 0
    const timeSinceLastAttempt = Date.now() - lastAttempt
    if (timeSinceLastAttempt < WebRTCManager.CONNECTION_COOLDOWN_MS) {
      console.log('[VIDEO-11] SKIP - Cooldown active for:', playerId,
        `(${timeSinceLastAttempt}ms since last attempt, need ${WebRTCManager.CONNECTION_COOLDOWN_MS}ms)`)
      getLogger().debug('Cooldown active for:', playerId, { timeSinceLastAttempt })
      return
    }

    if (!this.localStream) {
      console.log('[VIDEO-11] SKIP - No local stream')
      getLogger().warn('Cannot initiate connection without local stream')
      return
    }

    // Mark as pending and record attempt time
    this.pendingConnectionAttempts.add(playerId)
    this.lastConnectionAttempt.set(playerId, Date.now())

    // Safety timeout: Clear pending state after 15 seconds if connection doesn't complete
    // This prevents stale pending states from blocking future connection attempts
    setTimeout(() => {
      if (this.pendingConnectionAttempts.has(playerId)) {
        const pc = this.peerConnections.get(playerId)
        const state = pc?.connectionState
        if (state !== 'connected') {
          console.log('[VIDEO-11] Clearing stale pending connection for:', playerId, { state })
          this.pendingConnectionAttempts.delete(playerId)
        }
      }
    }, 15000)

    getLogger().info('Initiating WebRTC connection to:', playerId)

    const pc = this.createPeerConnection(playerId)
    this.peerConnections.set(playerId, pc)

    // Add local tracks
    this.addTracksToConnection(pc)

    // Create and send offer
    try {
      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)

      // VIDEO-12: Sending WebRTC offer
      console.log('[VIDEO-12] Sending WebRTC offer', {
        targetPlayerId: playerId,
        offerType: offer.type,
        hasSdp: !!offer.sdp,
        socketConnected: this.socket?.connected
      })

      getLogger().info('Sending WebRTC offer to:', playerId)
      this.socket?.emit('webrtc_offer', {
        target_player_id: playerId,
        offer: pc.localDescription,
      })
    } catch (error) {
      console.error('[VIDEO-11] Error creating offer:', error)
      getLogger().error('Error creating offer:', error)
    }
  }

  /**
   * Create a new RTCPeerConnection for a player
   */
  private createPeerConnection(playerId: string): RTCPeerConnection {
    getLogger().debug('Creating peer connection for:', playerId)
    const pc = new RTCPeerConnection(getIceConfiguration())

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        getLogger().debug('Sending ICE candidate to:', playerId)
        this.socket?.emit('webrtc_ice_candidate', {
          target_player_id: playerId,
          candidate: event.candidate,
        })
      }
    }

    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      const state = pc.connectionState
      getLogger().info('Connection state changed', { playerId, state })

      // Log for debugging tab switch issues
      console.log('[WEBRTC-STATE] Connection state changed', {
        playerId,
        state,
        hasDisconnectTimeout: this.disconnectTimeouts.has(playerId)
      })

      this.connectionStates.set(playerId, state)

      if (this.onConnectionStateChange) {
        this.onConnectionStateChange(playerId, state)
      }

      if (state === 'connected') {
        getLogger().info('Successfully connected to player', playerId)
        // Clear pending connection attempt - we're connected!
        this.pendingConnectionAttempts.delete(playerId)
        console.log('[WEBRTC-STATE] Connection established, cleared pending state', { playerId })

        // Cancel any pending disconnect timeout - connection recovered!
        const timeout = this.disconnectTimeouts.get(playerId)
        if (timeout) {
          console.log('[WEBRTC-STATE] Connection recovered, canceling disconnect timeout', { playerId })
          clearTimeout(timeout)
          this.disconnectTimeouts.delete(playerId)
        }
      } else if (state === 'disconnected') {
        // 'disconnected' is often temporary (tab switch, brief network issue)
        // Wait 10 seconds before cleaning up to allow recovery
        getLogger().warn('Connection temporarily disconnected from player', playerId)
        console.log('[WEBRTC-STATE] Disconnected - starting 10s recovery timer', { playerId })

        // Don't create duplicate timeouts
        if (!this.disconnectTimeouts.has(playerId)) {
          const timeout = setTimeout(() => {
            const currentState = this.peerConnections.get(playerId)?.connectionState
            console.log('[WEBRTC-STATE] Recovery timeout expired', {
              playerId,
              currentState,
              willCleanup: currentState !== 'connected'
            })

            if (currentState !== 'connected') {
              getLogger().warn('Connection did not recover after 10s, cleaning up', playerId)
              this.handleDisconnect(playerId)
            }
            this.disconnectTimeouts.delete(playerId)
          }, 10000) // 10 second recovery window

          this.disconnectTimeouts.set(playerId, timeout)
        }
      } else if (state === 'failed') {
        // 'failed' is terminal - clean up immediately
        getLogger().error('Connection failed to player', playerId)
        console.log('[WEBRTC-STATE] Connection FAILED - cleaning up immediately', { playerId })
        // Clear pending connection attempt on failure
        this.pendingConnectionAttempts.delete(playerId)
        this.handleDisconnect(playerId)
      }
    }

    // Handle incoming tracks (remote video/audio)
    pc.ontrack = (event) => {
      // VIDEO-16: Remote track received
      console.log('[VIDEO-16] ontrack fired', {
        fromPlayerId: playerId,
        trackKind: event.track.kind,
        trackId: event.track.id,
        trackReadyState: event.track.readyState,
        trackEnabled: event.track.enabled,
        streamId: event.streams[0]?.id,
        streamsCount: event.streams.length
      })

      getLogger().info('Received remote track from:', playerId,
        'kind:', event.track.kind, 'readyState:', event.track.readyState)

      if (event.track.readyState !== 'live') {
        console.log('[VIDEO-16] SKIP - Track is not live:', event.track.readyState)
        getLogger().warn('Track is not live, state:', event.track.readyState)
        return
      }

      let stream = this.remoteStreams.get(playerId)
      if (!stream) {
        stream = new MediaStream()
        this.remoteStreams.set(playerId, stream)
        getLogger().debug('Created new remote stream for:', playerId)
      }

      stream.addTrack(event.track)
      getLogger().debug(`Stream for ${playerId} now has ${stream.getTracks().length} tracks`)

      // Debounce callback - wait 100ms for other tracks
      clearTimeout(this.streamTimeouts.get(playerId))
      const timeout = setTimeout(() => {
        if (this.onRemoteStream && stream && stream.active) {
          // VIDEO-17: Notifying UI of remote stream
          console.log('[VIDEO-17] Notifying UI of remote stream', {
            playerId,
            streamId: stream.id,
            streamActive: stream.active,
            tracks: stream.getTracks().map(t => ({
              kind: t.kind,
              id: t.id,
              enabled: t.enabled,
              readyState: t.readyState
            }))
          })
          getLogger().info(`Notifying UI of stream for ${playerId} (${stream.getTracks().length} tracks)`)
          this.onRemoteStream(playerId, stream)
        }
        this.streamTimeouts.delete(playerId)
      }, 100)

      this.streamTimeouts.set(playerId, timeout)

      // Handle track ending
      event.track.onended = () => {
        getLogger().info('Track ended for:', playerId, 'kind:', event.track.kind)
        stream?.removeTrack(event.track)

        if (stream && stream.getTracks().length === 0) {
          getLogger().info('All tracks ended, removing stream for:', playerId)
          this.remoteStreams.delete(playerId)
        }
      }
    }

    // Monitor ICE connection state
    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState
      getLogger().info(`ICE connection state for ${playerId}:`, state)

      switch (state) {
        case 'connected':
        case 'completed':
          getLogger().info(`Peer connection established with ${playerId}`)
          break
        case 'disconnected':
          getLogger().warn(`Peer connection temporarily disconnected from ${playerId}`)
          break
        case 'failed':
          getLogger().error(`Peer connection failed with ${playerId}`)
          if (this.onConnectionFailed) {
            this.onConnectionFailed(playerId)
          }
          break
        case 'closed':
          getLogger().info(`Peer connection closed with ${playerId}`)
          this.remoteStreams.delete(playerId)
          if (this.onRemoteStreamEnded) {
            this.onRemoteStreamEnded(playerId)
          }
          break
      }
    }

    // Handle renegotiation when tracks are added/removed after connection established
    // This is CRITICAL for audio to work when enabled after video connection exists
    pc.onnegotiationneeded = async () => {
      getLogger().info(`Negotiation needed for ${playerId}`)
      console.log('[WEBRTC-AUDIO] onnegotiationneeded fired', {
        playerId,
        signalingState: pc.signalingState,
        connectionState: pc.connectionState
      })

      // Only renegotiate if connection is stable (not in the middle of another negotiation)
      if (pc.signalingState === 'stable') {
        try {
          await this.renegotiateConnection(playerId, pc)
        } catch (err) {
          getLogger().error(`Failed to renegotiate with ${playerId}:`, err)
          console.error('[WEBRTC-AUDIO] Renegotiation failed:', err)
        }
      } else {
        getLogger().debug(`Skipping renegotiation - signaling state is ${pc.signalingState}`)
      }
    }

    return pc
  }

  /**
   * Add local tracks to a peer connection
   */
  private addTracksToConnection(pc: RTCPeerConnection): void {
    if (!this.localStream) return

    // Log audio tracks specifically for debugging
    const audioTracks = this.localStream.getAudioTracks()
    const videoTracks = this.localStream.getVideoTracks()
    console.log('[AUDIO-TX-2] Adding tracks to connection', {
      audioTracks: audioTracks.map(t => ({ id: t.id, enabled: t.enabled, readyState: t.readyState })),
      videoTracks: videoTracks.map(t => ({ id: t.id, enabled: t.enabled, readyState: t.readyState })),
      existingSenders: pc.getSenders().map(s => ({ kind: s.track?.kind, trackId: s.track?.id }))
    })

    this.localStream.getTracks().forEach(track => {
      const senders = pc.getSenders()

      // Check if this exact track is already added
      const alreadyAdded = senders.some(s => s.track === track)
      if (alreadyAdded) {
        getLogger().debug('Track already added:', track.kind)
        console.log(`[AUDIO-TX-2] Track already added: ${track.kind}`)
        return
      }

      // Check if there's an existing sender without a track (or with different track of same kind)
      const emptySender = senders.find(s => !s.track)
      const sameKindSender = senders.find(s => s.track?.kind === track.kind && s.track !== track)

      if (emptySender) {
        getLogger().debug('Replacing empty sender with track:', track.kind)
        console.log(`[AUDIO-TX-2] Replacing empty sender with ${track.kind} track`)
        emptySender.replaceTrack(track).catch(err => {
          getLogger().error('Error replacing track:', err)
          console.error(`[AUDIO-TX-2] Error replacing track:`, err)
          pc.addTrack(track, this.localStream!)
        })
      } else if (sameKindSender) {
        // Replace existing track of same kind (e.g., switching audio devices)
        getLogger().debug('Replacing existing sender track:', track.kind)
        console.log(`[AUDIO-TX-2] Replacing existing ${track.kind} sender track`)
        sameKindSender.replaceTrack(track).catch(err => {
          getLogger().error('Error replacing same-kind track:', err)
          console.error(`[AUDIO-TX-2] Error replacing same-kind track:`, err)
        })
      } else {
        pc.addTrack(track, this.localStream!)
        getLogger().debug('Added track to peer connection:', track.kind)
        console.log(`[AUDIO-TX-2] Added NEW ${track.kind} track to peer connection`)
      }
    })
  }

  /**
   * Handle incoming WebRTC offer
   */
  private async handleOffer(fromPlayerId: string, offer: RTCSessionDescriptionInit): Promise<void> {
    // VIDEO-13: Received WebRTC offer
    console.log('[VIDEO-13] handleOffer called', {
      fromPlayerId,
      myPlayerId: this.myPlayerId,
      offerType: offer.type,
      hasExistingConnection: this.peerConnections.has(fromPlayerId),
      existingConnectionState: this.peerConnections.get(fromPlayerId)?.signalingState,
      hasLocalStream: !!this.localStream
    })

    getLogger().info('Handling offer from:', fromPlayerId)

    let pc = this.peerConnections.get(fromPlayerId)

    // Handle "glare" - both sides trying to connect at the same time
    if (pc && pc.signalingState !== 'stable') {
      const weArePolite = (this.myPlayerId || '') > fromPlayerId
      getLogger().debug('Glare detected!', { signalingState: pc.signalingState, weArePolite })

      if (weArePolite) {
        getLogger().debug('We are polite - rolling back our offer and accepting theirs')
        try {
          await pc.setLocalDescription({ type: 'rollback' })
        } catch (rollbackError) {
          getLogger().error('Rollback failed:', rollbackError)
          pc.close()
          this.peerConnections.delete(fromPlayerId)
          pc = undefined
        }
      } else {
        getLogger().debug('We are impolite - ignoring incoming offer')
        return
      }
    }

    if (!pc) {
      getLogger().debug('Creating new peer connection for:', fromPlayerId)
      pc = this.createPeerConnection(fromPlayerId)
      this.peerConnections.set(fromPlayerId, pc)
    }

    try {
      await pc.setRemoteDescription(new RTCSessionDescription(offer))
      getLogger().debug('Set remote description for:', fromPlayerId)

      // Process any ICE candidates that arrived before remote description was set
      await this.processPendingCandidates(fromPlayerId)

      // Add local tracks before creating answer
      if (this.localStream) {
        this.addTracksToConnection(pc)
      }

      const answer = await pc.createAnswer()
      await pc.setLocalDescription(answer)

      // VIDEO-14: Sending WebRTC answer
      console.log('[VIDEO-14] Sending WebRTC answer', {
        toPlayerId: fromPlayerId,
        answerType: pc.localDescription?.type,
        hasSdp: !!pc.localDescription?.sdp,
        socketConnected: this.socket?.connected
      })

      getLogger().info('Sending WebRTC answer to:', fromPlayerId)
      this.socket?.emit('webrtc_answer', {
        target_player_id: fromPlayerId,
        answer: pc.localDescription,
      })
    } catch (error) {
      getLogger().error('Error handling offer:', error)
    }
  }

  /**
   * Handle incoming WebRTC answer
   */
  private async handleAnswer(fromPlayerId: string, answer: RTCSessionDescriptionInit): Promise<void> {
    // VIDEO-15: Received WebRTC answer
    console.log('[VIDEO-15] handleAnswer called', {
      fromPlayerId,
      answerType: answer.type,
      hasConnection: this.peerConnections.has(fromPlayerId),
      connectionState: this.peerConnections.get(fromPlayerId)?.connectionState,
      signalingState: this.peerConnections.get(fromPlayerId)?.signalingState
    })

    const pc = this.peerConnections.get(fromPlayerId)
    if (!pc) {
      console.log('[VIDEO-15] ERROR - No peer connection for answer')
      getLogger().warn('No peer connection for answer from:', fromPlayerId)
      return
    }

    // Check signaling state - only accept answer if we're waiting for one
    if (pc.signalingState !== 'have-local-offer') {
      console.log('[VIDEO-15] SKIP - Not waiting for answer, state is:', pc.signalingState)
      getLogger().debug('Ignoring answer - already in state:', pc.signalingState)
      return
    }

    try {
      await pc.setRemoteDescription(new RTCSessionDescription(answer))
      // VIDEO-15B: Answer accepted
      console.log('[VIDEO-15B] Remote description set', {
        fromPlayerId,
        connectionState: pc.connectionState,
        signalingState: pc.signalingState,
        iceConnectionState: pc.iceConnectionState
      })
      getLogger().info('Set remote description for:', fromPlayerId, 'connection state:', pc.connectionState)
    } catch (error) {
      console.error('[VIDEO-15] Error handling answer:', error)
      getLogger().error('Error handling answer:', error)
    }
  }

  /**
   * Handle incoming ICE candidate
   * Queues the candidate if remote description hasn't been set yet
   */
  private async handleIceCandidate(fromPlayerId: string, candidate: RTCIceCandidateInit): Promise<void> {
    const pc = this.peerConnections.get(fromPlayerId)
    if (!pc) {
      getLogger().warn('No peer connection for ICE candidate from:', fromPlayerId)
      return
    }

    // Queue candidate if remote description not yet set (prevents InvalidStateError)
    if (!pc.remoteDescription) {
      if (!this.pendingCandidates.has(fromPlayerId)) {
        this.pendingCandidates.set(fromPlayerId, [])
      }
      this.pendingCandidates.get(fromPlayerId)!.push(candidate)
      getLogger().debug('Queued ICE candidate (remote description not yet set)', {
        playerId: fromPlayerId,
        queueSize: this.pendingCandidates.get(fromPlayerId)!.length
      })
      return
    }

    try {
      await pc.addIceCandidate(new RTCIceCandidate(candidate))
    } catch (error) {
      getLogger().error('Error adding ICE candidate:', error)
    }
  }

  /**
   * Process queued ICE candidates after remote description is set
   */
  private async processPendingCandidates(playerId: string): Promise<void> {
    const pc = this.peerConnections.get(playerId)
    if (!pc || !this.pendingCandidates.has(playerId)) return

    const candidates = this.pendingCandidates.get(playerId)!
    getLogger().debug('Processing queued ICE candidates', {
      playerId,
      count: candidates.length
    })

    for (const candidate of candidates) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate))
      } catch (error) {
        getLogger().error('Error adding queued ICE candidate:', error)
      }
    }
    this.pendingCandidates.delete(playerId)
  }

  /**
   * Handle peer disconnect
   */
  private handleDisconnect(playerId: string): void {
    getLogger().info('Peer disconnected:', playerId)
    console.log('[WEBRTC-DISCONNECT] handleDisconnect called', {
      playerId,
      hasConnection: this.peerConnections.has(playerId),
      hasStream: this.remoteStreams.has(playerId),
      hasLocalStream: !!this.localStream,
      playerVideoState: this.playerVideoStates.get(playerId)
    })

    // Clear any pending disconnect timeout
    const timeout = this.disconnectTimeouts.get(playerId)
    if (timeout) {
      clearTimeout(timeout)
      this.disconnectTimeouts.delete(playerId)
    }

    const pc = this.peerConnections.get(playerId)
    if (pc) {
      pc.close()
      this.peerConnections.delete(playerId)
    }

    this.remoteStreams.delete(playerId)

    if (this.onRemoteStreamEnded) {
      this.onRemoteStreamEnded(playerId)
    }

    // Auto-reconnect if we still have video and they still have video/audio
    const theirVideoState = this.playerVideoStates.get(playerId)
    const theyHaveMedia = theirVideoState?.video_enabled || theirVideoState?.audio_enabled
    if (this.localStream && theyHaveMedia) {
      // Check cooldown before scheduling reconnect
      const lastAttempt = this.lastConnectionAttempt.get(playerId) || 0
      const timeSinceLastAttempt = Date.now() - lastAttempt
      const remainingCooldown = Math.max(0, WebRTCManager.CONNECTION_COOLDOWN_MS - timeSinceLastAttempt)

      // Wait for cooldown + 1 second buffer
      const reconnectDelay = Math.max(1000, remainingCooldown + 1000)

      console.log('[WEBRTC-DISCONNECT] Scheduling auto-reconnect to', playerId,
        { reconnectDelay, timeSinceLastAttempt, theyHaveMedia })
      getLogger().info('Scheduling auto-reconnect to', playerId, { reconnectDelay })

      setTimeout(() => {
        // Re-check all conditions before reconnecting
        if (this.localStream &&
            !this.peerConnections.has(playerId) &&
            !this.pendingConnectionAttempts.has(playerId)) {
          console.log('[WEBRTC-DISCONNECT] Executing auto-reconnect to', playerId)
          this.initiateConnection(playerId)
        } else {
          console.log('[WEBRTC-DISCONNECT] Skipping auto-reconnect - conditions changed', {
            playerId,
            hasLocalStream: !!this.localStream,
            hasConnection: this.peerConnections.has(playerId),
            hasPending: this.pendingConnectionAttempts.has(playerId)
          })
        }
      }, reconnectDelay)
    }
  }

  /**
   * Get remote stream for a player
   */
  getRemoteStream(playerId: string): MediaStream | null {
    return this.remoteStreams.get(playerId) || null
  }

  /**
   * Get all remote streams
   */
  getRemoteStreams(): Map<string, MediaStream> {
    return new Map(this.remoteStreams)
  }

  /**
   * Get video state for a player
   */
  getPlayerVideoState(playerId: string): VideoState | null {
    return this.playerVideoStates.get(playerId) || null
  }

  /**
   * Get connection state for a player
   */
  getConnectionState(playerId: string): RTCPeerConnectionState | null {
    return this.connectionStates.get(playerId) || null
  }

  /**
   * Stop sharing video - removes local tracks but keeps receiving
   */
  stopSharing(): void {
    getLogger().info('Stopping video sharing (keeping incoming streams)')

    this.socket?.emit('video_state_changed', {
      video_enabled: false,
      audio_enabled: false,
    })

    // Remove local tracks from all peer connections
    this.peerConnections.forEach((pc, playerId) => {
      const senders = pc.getSenders()
      senders.forEach(sender => {
        if (sender.track) {
          getLogger().debug('Removing local track from connection to:', playerId)
          pc.removeTrack(sender)
        }
      })
    })

    this.localStream = null
    getLogger().info('Local tracks removed, still receiving from', this.remoteStreams.size, 'players')
  }

  /**
   * Clean up all connections
   */
  cleanup(): void {
    getLogger().info('Cleaning up WebRTC manager')

    // Stop sharing
    this.stopSharing()

    // Close all peer connections
    this.peerConnections.forEach((pc, playerId) => {
      getLogger().debug('Closing peer connection to:', playerId)
      pc.close()
    })
    this.peerConnections.clear()

    // Clear remote streams
    this.remoteStreams.forEach(stream => {
      stream.getTracks().forEach(track => track.stop())
    })
    this.remoteStreams.clear()

    // Clear timeouts
    this.streamTimeouts.forEach(timeout => clearTimeout(timeout))
    this.streamTimeouts.clear()

    // Clear disconnect recovery timeouts
    this.disconnectTimeouts.forEach(timeout => clearTimeout(timeout))
    this.disconnectTimeouts.clear()

    // Remove handlers
    this.removeSignalingHandlers()
    this.handlersSetup = false

    // Clear state
    this.playerVideoStates.clear()
    this.connectionStates.clear()
    this.otherPlayerIds = []
    this.pendingReconnections.clear()
    this.pendingConnectionAttempts.clear()
    this.lastConnectionAttempt.clear()

    getLogger().info('WebRTC manager cleaned up')
  }
}
